import shared from "../Shared/shared.module.css";
import { IconTool, IconAlertCircle, IconChevronsLeft, IconArrowRight } from "@tabler/icons-react";
import { abilities, ALL_SKILLS } from "../../../constants";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { talentProfile, talentSkills, talentRoles } from "../../../services/talentCalls";
import TagsInput from "../../../components/UI/TagsInput/tags";
import { errorHandler } from "../../../utils";
import FullScreenLoader from "../../../components/Loaders/FullScreen/loader";
import Select from "react-select";
import { getSelectStyle } from "../../../utils/functions";

const getRatingLabel = (value) => {
  if (value >= 80) return "Excellent";
  if (value >= 60) return "Very Good";
  if (value >= 40) return "Good";
  if (value >= 20) return "Fair";
  return "Poor";
};

const Stage4 = () => {
  const navigate = useNavigate();
  const [load, setLoad] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const [primary, setPrimary] = useState([]);
  const [secondary, setSecondary] = useState([]);
  const [allTalentRoles, setTalentRoles] = useState([]);
  const [roleError, setRoleError] = useState(false);
  const [range, setRange] = useState({
    teamManagement: 0, teamWork: 0, pressure: 0, collaboration: 0, planning: 0,
    organized: 0, clientRelationship: 0, creativity: 0, sales: 0, communication: 0, primaryRole: "",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fetchTalentRoles = async () => {
    try {
      const res = await talentRoles();
      if (res?.data?.data?.records) {
        setTalentRoles([...res?.data?.data?.records].sort((a, b) => a.name.localeCompare(b.name)));
      }
    } catch (_) {}
  };

  const extractSkillsFromResume = () => {
    try {
      const extractedDataString = localStorage.getItem("extractedResumeData");
      if (!extractedDataString) return;
      const extractedData = JSON.parse(extractedDataString);
      if (extractedData?.skills && Array.isArray(extractedData.skills)) {
        const technicalSkills = extractedData.skills.filter(
          (s) => s !== "•" && s !== "Technical:" && s !== "Professional:" && !s.includes("•") && s.trim().length > 1,
        );
        if (technicalSkills.length > 0) {
          setPrimary(technicalSkills.slice(0, 5));
          setSecondary(technicalSkills.slice(5));
        }
      }
    } catch (_) {}
  };

  const getUserProfile = async () => {
    setLoading(true);
    try {
      const res = await talentProfile();
      if (res?.data?.data?.talent) setData(res?.data?.data?.talent);
    } catch (_) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    extractSkillsFromResume();
    getUserProfile();
    fetchTalentRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (data?.primarySkills && primary.length === 0) setPrimary(data.primarySkills);
    if (data?.secondarySkills && secondary.length === 0) setSecondary(data.secondarySkills);
    if (data?.abilities) setRange((prev) => ({ ...prev, ...data.abilities }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const submit = async () => {
    if (!range?.primaryRole) {
      setRoleError(true);
      return errorHandler("Please select your primary role", "Error");
    }
    setRoleError(false);
    try {
      setLoad(true);
      const payload = {
        primarySkills: primary,
        secondarySkills: secondary,
        abilities: { ...range },
      };
      const res = await talentSkills(payload);
      if (res?.data?.success) navigate({ pathname: "/talent-onboarding", search: "stage=5" });
    } catch (error) {
      error?.response?.data
        ? errorHandler(error?.response?.data?.errors, "Error")
        : errorHandler("Network error: Something went wrong, Please check your internet connection and try again.", "Error");
    } finally {
      setLoad(false);
    }
  };

  return (
    <>
      {(loading || load) && <FullScreenLoader />}
      <div className={shared.panelHero}>
        <div className={shared.panelHeroLeft}>
          <div className={shared.panelHeroIcon}>
            <IconTool size={22} />
          </div>
          <div>
            <div className={shared.panelTitle}>Skills</div>
            <div className={shared.panelSubtitle}>Showcase your expertise and self-assessed abilities</div>
          </div>
        </div>
      </div>

      <div className={shared.panelBody}>
        <div className={shared.formField} style={{ marginBottom: 22 }}>
          <div className={shared.sectionLabel}>Primary Skills</div>
          <TagsInput required defaultData={ALL_SKILLS} setData={setPrimary} data={primary} maxTags={5} />
        </div>

        <div className={shared.formField} style={{ marginBottom: 26 }}>
          <div className={shared.sectionLabel}>Secondary Skills</div>
          <TagsInput setData={setSecondary} data={secondary} defaultData={ALL_SKILLS} maxTags={5} />
        </div>

        <div className={shared.sectionLabel} style={{ marginBottom: 4 }}>Abilities</div>
        <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginBottom: 18 }}>
          Drag the bar to give us more insights about how your abilities measure up
        </div>

        <div className={shared.formField} style={{ marginBottom: 24 }}>
          <label className={shared.formLabel}>
            Primary Role<span className={shared.req}>*</span>
          </label>
          <Select
            styles={getSelectStyle("auto")}
            placeholder="Select a primary role…"
            value={allTalentRoles.map((r) => ({ label: r.label || r.name, value: r.name })).find((o) => o.value === range.primaryRole) || null}
            onChange={(selected) => {
              setRange((prev) => ({ ...prev, primaryRole: selected ? selected.value : "" }));
              setRoleError(false);
            }}
            isSearchable
            isClearable
            name="primaryRole"
            options={allTalentRoles.map((r) => ({ label: r.label || r.name, value: r.name }))}
          />
          {roleError && (
            <div className={shared.fieldErr}>
              <IconAlertCircle size={13} /> Please select a primary role.
            </div>
          )}
        </div>

        {abilities.map((item) => {
          const currentValue = range[item.name] || 0;
          return (
            <div className={shared.abilityBlock} key={item.name}>
              <div className={shared.abilityRowLabel}>
                <span className={shared.abilityName}>{item.value}</span>
                <span className={shared.abilityValue}>
                  {currentValue} <span style={{ color: "#16a34a", fontWeight: 600, marginLeft: 3 }}>{getRatingLabel(currentValue)}</span>
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                step="1"
                value={currentValue}
                onChange={(e) => setRange((prev) => ({ ...prev, [item.name]: parseInt(e.target.value, 10) }))}
                className={shared.abilitySlider}
              />
              <div className={shared.abilityScale}>
                <span>0</span>
                <span>25</span>
                <span>50</span>
                <span>75</span>
                <span>100</span>
              </div>
            </div>
          );
        })}
      </div>

      <div className={shared.panelFooter}>
        <button type="button" className={shared.btnBack} onClick={() => navigate("?stage=3")}>
          <IconChevronsLeft size={16} /> Back
        </button>
        <div className={shared.footerActions}>
          <button type="button" className={shared.btnPrimary} onClick={submit} disabled={load}>
            Next <IconArrowRight size={15} />
          </button>
        </div>
      </div>
    </>
  );
};

export default Stage4;
